import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormGroup, FormControl, Validators, AbstractControl } from '@angular/forms';
import { ReceitasBrutasAnterioresService } from '../../../services/receitas-brutas-anteriores.service';
import { Router, ActivatedRoute } from '@angular/router';
import { ToastService } from '../../../services/toast.service';
import { SeveridadeToast } from '../../../models/enums/severidade-toast.enum';
import { SpinnerService } from '../../../services/spinner.service';
import { ReceitaBrutaAnterior } from '../../../models/receitas-brutas-anteriores.model';
import { catchError, finalize } from 'rxjs/operators';
import { JwtHelper } from '../../../helpers/jwtDecode.helper';
import { of } from 'rxjs';
import { FormataDataHelper } from '../../../helpers/formata-data.helper';

@Component({
  selector: 'app-receitas-brutas-anteriores-editar',
  templateUrl: './receitas-brutas-anteriores-editar.component.html',
  styleUrls: ['./receitas-brutas-anteriores-editar.component.scss']
})
export class ReceitasBrutasAnterioresEditarComponent implements OnInit {
  @Output() tituloChange = new EventEmitter<string>();
  @Output() iconeChange = new EventEmitter<string>();

  receitaBrutaForm: FormGroup = new FormGroup({
    periodoApuracao: new FormControl(''),
    pa: new FormControl(''),
    valorInterno: new FormControl('', [Validators.min(0)]),
    valorExterno: new FormControl('', [Validators.min(0)]),
    usuarioId: new FormControl('', [Validators.required]),
  }, { validators: this.atLeastOneValidator(['valorInterno', 'valorExterno']) });

  receitaBrutaId: string | null = null;
  userId: string | null = null;
  receitaBruta: ReceitaBrutaAnterior;


  constructor(
    private receitaBrutaService: ReceitasBrutasAnterioresService,
    private route: ActivatedRoute,
    private router: Router,
    private spinnerService: SpinnerService,
    private toastService: ToastService,
    private jwtHelper: JwtHelper
  ) { }

  ngOnInit(): void {
    this.tituloChange.emit('Editar Receita Bruta Anterior');
    this.iconeChange.emit("pi pi-chart-bar");

    this.userId = this.jwtHelper.getUserIdFromToken();
    this.receitaBrutaForm.get('usuarioId')?.setValue(this.userId);

    this.spinnerService.show('Carregando dados da receita bruta...');
    this.loadInitialData();

    const paValue = this.receitaBrutaForm.get('pa')?.value;
    if (paValue) {
      this.receitaBrutaForm.get('pa')?.setValue(this.formatCompetencia(paValue));
    }
  }


  private loadInitialData(): void {
    this.route.paramMap.subscribe((params) => {
      const id = params.get('id');
      if (id) {
        this.receitaBrutaId = id;
        this.loadReceitaBruta();
      } else {
        this.spinnerService.hide();
      }
    });
  }

  private loadReceitaBruta(): void {
    if (this.receitaBrutaId !== null) {
      this.receitaBrutaService
        .obterReceitaBrutaAnterior(this.receitaBrutaId)
        .pipe(
          catchError((error) => {
            this.toastService.setMensagemToast(
              `Erro ao carregar receita bruta.`,
              SeveridadeToast.Error
            );
            return of(null);
          }),
          finalize(() => this.spinnerService.hide())
        )
        .subscribe((receitaBruta: ReceitaBrutaAnterior | null) => {
          if (receitaBruta) {
            this.receitaBrutaForm.patchValue(receitaBruta);
          }
        });
    }
  }

  private atLeastOneValidator(fields: string[]) {
    return (form: AbstractControl) => {
      const hasAtLeastOne = fields.some(field => form.get(field)?.value);
      return hasAtLeastOne ? null : { atLeastOneRequired: true };
    };
  }

  onSubmit() {
    if (this.receitaBrutaForm.valid) {
      const formValue = this.receitaBrutaForm.value;

      const valorInterno = formValue.valorInterno || 0;
      const valorExterno = formValue.valorExterno || 0;

      this.receitaBruta = {
        id: this.receitaBrutaId!,
        pa: formValue.pa || '',
        periodoApuracao: formValue.periodoApuracao,
        valorInterno: valorInterno,
        valorExterno: valorExterno,
        usuarioId: this.userId!,
      };

      this.spinnerService.show('Atualizando receita bruta...');
      this.receitaBrutaService
        .atualizar(this.receitaBrutaId!, this.receitaBruta)
        .pipe(finalize(() => this.spinnerService.hide()))
        .subscribe({
          next: () => {
            this.toastService.setMensagemToast(
              `Receita bruta editada com sucesso!`,
              SeveridadeToast.Success
            );
            this.router.navigate(['/receitas-brutas-anteriores']);
            this.spinnerService.hide();
          },
          error: () => {
            this.toastService.setMensagemToast(
              `Erro ao editar a receita bruta.`,
              SeveridadeToast.Error
            );
            this.spinnerService.hide();
          }
        });
    }
  }

  formatCompetencia(pa: number | undefined): string {
    return FormataDataHelper.formatarCompetencia(pa);
  }

  onCancel() {
    this.router.navigate(['/receitas-brutas-anteriores']);
  }
}
